/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-var */

import { LoginModel } from '@models';
import {
  AbpUserConfigurationService,
  ClarityService,
  SessionService,
  TenantLoginInfoDto,
  TokenAuthService,
  TwoFactorAuthService,
  UserLoginInfoDto,
} from '@services';
import type { VerifyTwoFactorLoginInput } from '@services';

import { AppConsts, clearStoredSession, TWO_FACTOR_TRUST_TOKEN_KEY } from '@lib';
import { Utils } from '@utils';
import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';

declare const abp: any;


class AuthenticationStore {
  currentUser: UserLoginInfoDto | undefined;
  currentTenant: TenantLoginInfoDto | undefined;

  /** Non-null when the server returned requiresTwoFactorVerification = true. */
  pendingTwoFactorChallengeToken: string | undefined = undefined;
  /** The 2FA method for the pending challenge: 'Email', 'Sms', or 'Authenticator'. */
  pendingTwoFactorMethod: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);
    this.loadCurrentUser();
    this.loadCurrentTenant();
  }

  get isAuthenticated(): boolean {
    if (!abp.auth.getToken() || !abp.session.userId) return false;

    return true;
  }

  public async login(model: LoginModel) {
    try {
      const trustToken = localStorage.getItem(TWO_FACTOR_TRUST_TOKEN_KEY) ?? undefined;

      const result = await TokenAuthService.authenticate({
        userNameOrEmailAddress: model.userNameOrEmailAddress,
        password: model.password,
        rememberClient: model.rememberMe,
        isMobile: false,
        twoFactorTrustToken: trustToken,
      });

      // 2FA challenge — no token issued yet, redirect to verification screen.
      if (result.requiresTwoFactorVerification) {
        runInAction(() => {
          this.pendingTwoFactorChallengeToken = result.twoFactorChallengeToken;
          this.pendingTwoFactorMethod = result.twoFactorMethod;
        });
        return result;
      }

      await this._finalizeLogin(result);
      return result;
    } catch (error: any) {
      ClarityService.event('login-failed');
      // Error notification is already handled by HTTP interceptor
      throw error;
    }
  }

  public async verifyTwoFactorCode(input: Omit<VerifyTwoFactorLoginInput, 'challengeToken'>) {
    if (!this.pendingTwoFactorChallengeToken) {
      throw new Error('No pending 2FA challenge.');
    }

    const result = await TwoFactorAuthService.verifyLoginCode({
      ...input,
      challengeToken: this.pendingTwoFactorChallengeToken,
    });

    if (result.trustToken) {
      localStorage.setItem(TWO_FACTOR_TRUST_TOKEN_KEY, result.trustToken);
    }

    runInAction(() => {
      this.pendingTwoFactorChallengeToken = undefined;
    });

    await this._finalizeLogin(result);
    return result;
  }

  private async _finalizeLogin(result: {
    accessToken: string;
    encryptedAccessToken: string;
    expireInSeconds: number;
    userId: number;
    tenantId: number | null;
  }) {
    ClarityService.identify(
      result.userId.toString(),
      undefined,
      'login-success',
      undefined
    );

    ClarityService.setTag('user-type', result.tenantId ? 'host-user' : 'tenant-user');
    ClarityService.event('user-logged-in');

    const tokenExpireDate = dayjs().add(result.expireInSeconds, 'second').toDate();
    abp.auth.setToken(result.accessToken, tokenExpireDate);
    abp.utils.setCookieValue(
      AppConsts.authorization.encrptedAuthTokenName,
      result.encryptedAccessToken,
      tokenExpireDate,
      abp.appPath
    );

    await Promise.all([
      AbpUserConfigurationService.getAll().then((data) => {
        Utils.extend(true, abp, data.data.result);
      }),
      SessionService.getCurrentLoginInformations().then((current) => {
        runInAction(() => {
          this.currentUser = current.user;
          this.currentTenant = current.tenant;
        });
        localStorage.setItem('currentUser', JSON.stringify(current.user));
        localStorage.setItem('currentTenant', JSON.stringify(current.tenant));
      }),
    ]);
  }

  loadCurrentUser() {
    const currentUser = localStorage.getItem('currentUser');

    if (!currentUser) return;

    runInAction(() => {
      this.currentUser = JSON.parse(currentUser);
    });
  }

  loadCurrentTenant() {
    const currentTenant = localStorage.getItem('currentTenant');

    if (!currentTenant) return;

    runInAction(() => {
      this.currentTenant = JSON.parse(currentTenant);
    });
  }

  logout() {
    // The token lives in a cookie, so clearing storage alone left the browser holding a
    // perfectly usable session: going back to a page put the user straight back in.
    clearStoredSession();
  }
}

export default AuthenticationStore;
